import React from 'react';
import { Link } from '@inertiajs/react';
import { Seo } from '~/marketing/Seo';
import { Card, Button } from '~/marketing/ui';
import { Stat } from '~/marketing/Stat';
import { routes } from '~/marketing/routes';
import { withMarketingLayout } from '~/marketing/MarketingLayout';
import { quoted as Q, SCHEDULER_LIVE } from '~/marketing/marketingData';

/* Class 2 strings come from the data module verbatim. Nothing on this page
   rewords one; a string that reads badly here is a change request. */
const sec = { display: 'grid', gap: 'var(--space-5)' };
const rule = { borderBottom: '1px solid var(--border-subtle)', paddingBottom: 'var(--space-3)' };
const body = { color: 'var(--text-body)', maxWidth: '70ch', margin: 0 };
const H2 = ({ children }) => <div style={rule}><h2 style={{ fontSize: 'var(--fs-h2)' }}>{children}</h2></div>;

/* The per-engine cadence labels are scheduler-dependent; with the scheduler
   off, the cards name the engine and nothing about how often it is asked.
   **The counts are `Collection::Coverage`'s**: ChatGPT and Claude take the
   first four questions, the who-to-hire ones, and the rest take all twenty.
   They read "focused set" and "full set" until 2026-10-26, a term the page
   explained two sections later. */
const ENGINES = [
  ['ChatGPT', '4 who-to-hire questions · asked 3 times each'],
  ['Claude', '4 who-to-hire questions · asked 3 times each'],
  ['Gemini', 'all 20 questions · asked 3 times each'],
  ['Perplexity', 'all 20 questions · asked 3 times each'],
  ['Google AI Overviews', 'all 20 questions · asked 3 times each'],
];
// Steps 1, 2 and 4 corrected 2026-10-26: ChatGPT and Claude are asked a
// focused set rather than every question, and matching allows for spacing,
// punctuation, legal suffixes and the names a business adds — not for
// abbreviations, which only an added name covers.
const STEPS = [
  'Write the questions for your industry and city, in the words a customer would use.',
  'Ask the questions of all five AI assistants, three times each, the same way every week.',
  'Extract every business named and every source cited from each answer.',
  'Match each name against your business, allowing for spacing, punctuation, legal suffixes and any other names you give us.',
  'Report counts across many answers, with the denominator shown beside every figure.',
  'Stamp the report with its collection date, so a figure can always be placed in time.',
];

export default function Methodology() {
  return (
    <>
      <Seo
        title="Methodology"
        description="Which AI assistants we ask, how often, and how an answer becomes a number — published in full, so each figure traces to the answers behind it."
        path={routes.methodology}
      />
      <main className="sp-page" style={{ paddingBlock: 'clamp(40px,5vw,80px)', display: 'grid', gap: 'var(--space-16)' }}>
        <header style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: '66ch' }}>
          <span className="sp-label">Methodology</span>
          <h1 style={{ fontSize: 'var(--fs-display-2)', lineHeight: 1.06 }}>How we measure, from the question asked to the number you act on.</h1>
          <p style={{ color: 'var(--text-body)', fontSize: 'var(--fs-body-lg)', margin: 0 }}>This page is the whole method: which questions, which AI assistants, how often, and what each number counts. We publish all of it, because a number is worth acting on when you know exactly what it is made of.</p>
        </header>

        <section style={sec}>
          <H2>The AI assistants we ask</H2>
          <p style={body}>{Q.engine_coverage}</p>
          <div style={{ display: 'grid', gap: 'var(--space-5)', gridTemplateColumns: 'repeat(auto-fit,minmax(min(220px,100%),1fr))' }}>
            {ENGINES.map(([n, cadence]) => (
              <Card key={n}><div style={{ display: 'grid', gap: 8 }}>
                <span style={{ fontFamily: 'var(--font-display)', fontWeight: 'var(--fw-semibold)', fontSize: 'var(--fs-h4)', letterSpacing: 'var(--tr-h2)' }}>{n}</span>
                {SCHEDULER_LIVE && <span className="sp-mono" style={{ fontSize: 12, color: 'var(--teal)' }}>{cadence}</span>}
              </div></Card>
            ))}
          </div>
          <Card variant="inset">
            <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
              <span className="sp-label">How Google is measured</span>
              <p style={{ ...body, fontSize: 'var(--fs-body-sm)', maxWidth: '66ch' }}>{Q.google_method}</p>
              <p style={{ ...body, fontSize: 'var(--fs-body-sm)', maxWidth: '66ch' }}>{Q.google_none}</p>
            </div>
          </Card>
        </section>

        <section style={sec}>
          <H2>We ask through APIs, so every week is comparable</H2>
          <p style={body}>{Q.measurement_basis}</p>
          {/* Two tiles and a paragraph went 2026-10-26. The tiles repeated the
              figures in the sentence above with no date and no AI assistant
              named; the paragraph said the businesses named ahead of you in the
              app are the same ones named here, which was never measured. */}
        </section>

        <section style={sec}>
          <H2>The same question gets different answers</H2>
          <p style={body}>{Q.variability}</p>
          {/* "we count rather than screenshot" swiped at a competitor practice,
              and "a far stronger thing to hold" is cadence rather than a claim.
              What is left is the fact, which is the thing worth saying. */}
          <p style={body}>It is also why every figure on this site is a count across many answers rather than one of them, and carries its own denominator, so you can see how wide the evidence is before you act on it.</p>
        </section>

        <section style={sec}>
          <H2>What our figures cover</H2>
          {/* Until 2026-10-12 this ended "Every trade-level percentage currently
              shown on this site is a placeholder", which had stopped being true
              when the trade tiles were measured. */}
          {/* Retitled from "What we have not measured yet" 2026-10-26, on the
              founder's review: it described the corpus as it stood before med
              spa, PI law and dental were measured on the benchmark's three AI
              assistants, and closed on what a bake-off cannot tell us, which
              answered a question nobody reading it had asked. */}
          <p style={body}>Our published figures come from thirteen local industries, measured on Perplexity, Gemini and Google AI Overviews. Ten are written up on our <Link href={routes.blog}>blog</Link>, and every one has its own page.</p>
          <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: '70ch' }}>
            <Stat id="hvac_sample" mode="figure" figureSize="var(--fs-h2)" />
            <Stat id="plumbing_sample" mode="figure" figureSize="var(--fs-h2)" />
          </div>
        </section>

        {SCHEDULER_LIVE && (
          <section style={sec}>
            <H2>How often we ask, and how much</H2>
            <p style={body}>{Q.sampling}</p>
            <p style={body}>{Q.weekly_not_daily}</p>
          </section>
        )}

        <section style={sec}>
          <H2>Procedure, in order</H2>
          <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: '74ch' }}>
            {STEPS.map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: 16 }}>
                <span className="sp-mono" style={{ fontSize: 12, color: 'var(--teal)', width: 64, flex: 'none', paddingTop: 4 }}>STEP {i + 1}/{STEPS.length}</span>
                <span style={{ fontSize: 'var(--fs-body-sm)', color: 'var(--text-body)' }}>{s}</span>
              </div>
            ))}
          </div>
        </section>

        <section style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-8)', display: 'flex', gap: 'var(--space-6)', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div style={{ display: 'grid', gap: 6 }}>
            <h2 style={{ fontSize: 'var(--fs-h2)' }}>See where you stand with the free check.</h2>
            <span className="sp-caption">One question, five AI assistants, the report emailed to you.</span>
          </div>
          <Button size="lg" href={routes.grader}>Check your visibility</Button>
        </section>
      </main>
    </>
  );
}

Methodology.layout = withMarketingLayout;
